import { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Paperclip } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { tplPath } from "@/lib/forms";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { PageHeader } from "@/components/StatusBadge";
import { UploadBox } from "@/components/forms/UploadBox";
import { FieldEditor, cleanFieldsForSave } from "@/components/forms/FieldEditor";

export default function TemplateEditor() {
  const { id } = useParams();
  const nav = useNavigate();
  const [t, setT] = useState({ name: "", description: "", fields: [] });
  const [upload, setUpload] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (id) api.get(`/forms/templates/${id}`).then((r) => setT(r.data)).catch((e) => toast.error(errMsg(e))); }, [id]);
  const extract = async (file) => {
    setBusy(true);
    const fd = new FormData(); fd.append("file", file);
    try {
      const r = (await api.post("/forms/extract-fields", fd, { timeout: 300000 })).data;
      setT({ ...t, name: t.name || r.name, fields: r.fields }); setUpload(r.upload);
      toast.success(`${r.fields.length} fields found — review them below`);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const save = async () => {
    setBusy(true);
    const body = { name: t.name, description: t.description, fields: cleanFieldsForSave(t.fields), source_upload_id: upload?.upload_id };
    try {
      const r = id ? await api.put(`/forms/templates/${id}`, body) : await api.post("/forms/templates", body);
      toast.success("Template saved");
      nav(tplPath(r.data));
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const back = id ? (t.kind ? tplPath(t) : `/forms/${id}`) : "/forms";
  return (
    <div className="max-w-4xl">
      <PageHeader eyebrow="Forms & Data" title={id ? `Edit fields · ${t.name}` : "New form template"} testid="template-editor-title">
        <Button variant="outline" asChild data-testid="template-back"><Link to={back}><ArrowLeft className="w-4 h-4 mr-1" />Back</Link></Button>
      </PageHeader>
      <div className="space-y-5">
        <UploadBox onFile={extract} busy={busy} title={id ? "Upload a newer version of the form to re-read its fields" : "Upload the blank form"} testid="template-upload" />
        {upload && <div className="text-xs text-slate-600 flex items-center gap-1.5" data-testid="template-upload-name"><Paperclip className="w-3.5 h-3.5" />{upload.filename} will be kept with the template</div>}
        <div className="rounded-lg border border-slate-200 bg-white shadow-sm p-5 space-y-3">
          <div><label className="text-xs font-semibold text-slate-600">Template name</label>
            <Input value={t.name} onChange={(e) => setT({ ...t, name: e.target.value })} className="bg-white" data-testid="template-name-input" /></div>
          <div><label className="text-xs font-semibold text-slate-600">Description (optional)</label>
            <Textarea rows={2} value={t.description || ""} onChange={(e) => setT({ ...t, description: e.target.value })} className="bg-white" data-testid="template-description-input" /></div>
        </div>
        <div>
          <div className="text-sm font-semibold text-slate-900 mb-2">Fields {t.fields.length > 0 && <span className="text-slate-500 font-normal">· review what AI proposed</span>}</div>
          <FieldEditor fields={t.fields} onChange={(fields) => setT({ ...t, fields })} />
        </div>
        <div className="flex justify-end gap-2 sticky bottom-3">
          <Button onClick={save} disabled={busy || !t.name.trim() || !cleanFieldsForSave(t.fields).length} className="shadow-lg" data-testid="template-save-button">Save template</Button>
        </div>
      </div>
    </div>
  );
}
